import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getDomainsQuery,
  getGmailDeletionModels,
  saveGmailDeletionModel,
  updateGmailDeletionModel,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useStore } from '../../state/zustand-store';
import { GmailDeletionModel } from '../../../../types';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../../shared/simple-form-page';
import { pagesConfig } from '../../../shared/pages-config';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';

import { useNavigate, useParams } from 'react-router-dom';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import FormSkeleton from './form-step/form-skeleton';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import StepReview from './form-step/step-review';
import StepSources from './form-step/step-sources';
import StepConfiguration from './form-step/step-configuration';
import SharedFormStepper, {
  StepConfig,
} from '../../../shared/shared-form-stepper';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';

export const searchOptions = [
  { displayValue: 'All Mail', value: 'in:all' },
  { displayValue: 'Inbox', value: 'in:inbox' },
  { displayValue: 'Starred', value: 'is:starred' },
  { displayValue: 'Sent Mail', value: 'in:sent' },
  { displayValue: 'Drafts', value: 'in:drafts' },
  { displayValue: 'Chats', value: 'in:chats' },
  { displayValue: 'Spam', value: 'in:spam' },
  { displayValue: 'Trash', value: 'in:trash' },
  { displayValue: 'Mail & Spam & Trash', value: 'in:anywhere' },
  { displayValue: 'Read Mail', value: 'is:read' },
  { displayValue: 'Unread Mail', value: 'is:unread' },
  {
    displayValue: 'Social',
    value: 'category:social',
  },
  { displayValue: 'Promotions', value: 'category:promotions' },
  { displayValue: 'Updates', value: 'category:updates' },
  { displayValue: 'Forums', value: 'category:forums' },
];

const ConfigurationSchema = z.object({
  whereToSearch: z.string().min(1, 'Please select where to search.'),
  from: z.string().catch(''),
  to: z.string().catch(''),
  subject: z.string().catch(''),
  hasTheWords: z.string().catch(''),
  doesntHaveTheWords: z.string().catch(''),
  type: z.string().catch('message'),
  created: z.number(),
  converted: z.boolean().catch(false),
  searchString: z.string().catch(''),
  lastRun: z.number().catch(0),
  lastUpdated: z.number().catch(0),
  createdBy: z.string().catch(''),
  updatedBy: z.string().catch(''),
  primaryDomain: z.string().catch(''),
});

const SourcesSchema = z.object({
  sourceType: z.enum(['User', 'Group', 'OrgUnit', 'Domain']),

  sources: z.array(z.string().min(1)).min(1, 'Source is required.'),
});

const GmailDeletionSchema = ConfigurationSchema.and(SourcesSchema);

export type GmailDeletionFormValues = z.infer<typeof GmailDeletionSchema>;

export default function GmailDeletionFormPage() {
  const { changeLogRoles } = useUserSettings();
  const { id } = useParams();
  const selectedRowId = Number(id);

  const { data: domains } = useQuery(getDomainsQuery());
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const [activeStep, setActiveStep] = useState(0);

  const steps: StepConfig[] = [
    {
      label: 'Configuration',
      schema: ConfigurationSchema,
    },
    {
      label: 'Sources',
      schema: SourcesSchema,
    },
    {
      label: 'Review',
    },
  ];

  const {
    data: gmailDeletionModels,
    isLoading: gmailDeletionLoading,
    isFetching: gmailDeletionFetching,
  } = useQuery({
    queryKey: [UserKeys.GmailDeletionModels],
    queryFn: () => getGmailDeletionModels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gmail deletion models'),
    },
  });

  const selectedGmailDeletion = gmailDeletionModels?.find(
    (gmailDeletion) => gmailDeletion.created === selectedRowId
  );

  const isLoading = gmailDeletionLoading || gmailDeletionFetching;
  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => (selectedRowId ? true : false))
  );

  const values: GmailDeletionFormValues = selectedGmailDeletion
    ? selectedGmailDeletion
    : {
        sourceType: 'User',
        sources: [],
        type: 'message',
        lastRun: 0,
        created: 0,
        lastUpdated: 0,
        createdBy: '',
        updatedBy: '',
        primaryDomain: '',
        whereToSearch: 'in:all',
        from: '',
        to: '',
        subject: '',
        hasTheWords: '',
        doesntHaveTheWords: '',
        converted: false,
        searchString: '',
      };

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = useForm<GmailDeletionFormValues>({
    resolver: zodResolver(GmailDeletionSchema),
    values,
  });

  const handleCancel = () => {
    navigate(`/${pagesConfig.gmailDeletion.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<GmailDeletionModel>) =>
      selectedGmailDeletion
        ? updateGmailDeletionModel(data, changeLogMessage)
        : saveGmailDeletionModel(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: selectedGmailDeletion
          ? Messages.Success.Updated('Gmail deletion job')
          : Messages.Success.Added('Gmail deletion job'),
      });

      queryClient.setQueryData(
        [UserKeys.GmailDeletionModels],
        selectedGmailDeletion
          ? (oldData: GmailDeletionModel[]) => {
              const newList = oldData.filter(
                (model) => model.created !== selectedGmailDeletion.created
              );
              return [...newList, data];
            }
          : (oldData: GmailDeletionModel[]) => {
              return [...oldData, data];
            }
      );

      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.gmailDeletion.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: selectedGmailDeletion
          ? Messages.Errors.Network.Updating('gmail deletion')
          : Messages.Errors.Network.Creating('gmail deletion'),
      });
    },
    onSettled: () => {
      if (selectedRowId) {
        createEvent(
          analytics.import_export.gmail_deletion.create_gmail_deletion,
          {
            module: ModuleList.modules.importExport.name,
            section: ModuleList.modules.importExport.sections.gmailDeletion,
          }
        );
      } else {
        createEvent(
          analytics.import_export.gmail_deletion.update_gmail_deletion,
          {
            module: ModuleList.modules.importExport.name,
            section: ModuleList.modules.importExport.sections.gmailDeletion,
          }
        );
      }
    },
  });

  const onSubmit: SubmitHandler<GmailDeletionFormValues> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CreateGmailSearch.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const data = getValues();

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.importExport.name },
        {
          label: pagesConfig.gmailDeletion.name,
          path: pagesConfig.gmailDeletion.link,
        },
      ]}
    >
      <SimpleFormPage
        title={
          !selectedGmailDeletion
            ? pagesConfig.createGmailDeletionFormPage.name
            : pagesConfig.editGmailDeletionFormPage.name
        }
        stepper={
          <SharedFormStepper<GmailDeletionFormValues> {...formStepperProps} />
        }
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && <FormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Configuration' && (
              <StepConfiguration control={control} />
            )}
            {!isLoading && steps[activeStep].label === 'Sources' && (
              <StepSources
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                domains={domains || []}
                trigger={trigger}
                clearErrors={clearErrors}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepReview selectedGmailDeletion={data} />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={
          !selectedGmailDeletion
            ? pagesConfig.createGmailDeletionFormPage.name
            : 'Update Gmail Deletion'
        }
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Gmail Deletion"
        pageMode={selectedRowId ? 'edit' : 'create'}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newGmailDeletionModel: Partial<GmailDeletionModel> = {
            ...getValues(),
          };
          mutate(newGmailDeletionModel);
        }}
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
